import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Button } from '../../components/Button';
import { IconName } from '../../components/Icon';
import { ButtonVariant } from '../../config/colors';
import { LinkSignUp } from '../../components/LinkSignUp';
import {
  createNostrAuthProof,
  getBrowserNostrSigner,
  getNostrLoginUrl,
  isNostrExtensionInstalled,
  NostrAuthError,
} from '../../utils/NostrUtils';
import { authRepository } from '../../repositories/AuthRepository';
import { useAuth } from '../../hooks/useAuth';
import { useConfirm } from '../../hooks/useConfirm';
import { UserNotFoundOnLogin } from '../../repositories/Repository';

const ChooseAuthType: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [hasNostrExtension, setHasNostrExtension] = useState(false);
  const [isNostrSubmitting, setIsNostrSubmitting] = useState(false);
  const nostrSubmissionInFlight = React.useRef(false);
  const lastNostrProofTimestamp = React.useRef<number | undefined>(undefined);
  const {setCredentialsOnLogin} = useAuth();
  const { openConfirm } = useConfirm();

  useEffect(() => {
    setHasNostrExtension(isNostrExtensionInstalled());
    
    setTimeout(() => {
      setHasNostrExtension(isNostrExtensionInstalled());
    }, 1000);
  }, []);

  const handleNip07Auth = async (isSignup: boolean) => {
    if (nostrSubmissionInFlight.current) {
      return;
    }

    nostrSubmissionInFlight.current = true;
    setIsNostrSubmitting(true);

    try {
      const signer = getBrowserNostrSigner();
      if (!signer) {
        throw new NostrAuthError('No Nostr browser signer is available.');
      }

      const loginUrl = getNostrLoginUrl();
      const { event } = await createNostrAuthProof({
        signer,
        isSignup,
        loginUrl,
        afterCreatedAt: lastNostrProofTimestamp.current,
      });
      lastNostrProofTimestamp.current = event.created_at;

      const { credentials, pleb } = await authRepository.loginWithNip07(event, isSignup);
      await setCredentialsOnLogin(credentials, pleb);
      navigate('/home/chat');
    } catch (error: any) {
      if(error instanceof UserNotFoundOnLogin)
      {
        openConfirm({
          title: "Npub not found",
          message: "Your npub does not have an account",
          buttons: [
            { label: "Create account", href: import.meta.env.VITE_LINK_SIGNUP, target: "_blank", variant: ButtonVariant.inverted },
            { label: "Login with different npub", onClick: () => {}, variant: ButtonVariant.transparent }
          ],
        });
      }
      else
        openConfirm({
          message: (
            error instanceof NostrAuthError
              ? error.message
              : error.response?.data?.message ?? 'Nostr verification failed. Please try again.'
          ),
          buttons: [
            { label: "OK", onClick: () => {}, variant: ButtonVariant.inverted },
          ],
        });
    } finally {
      nostrSubmissionInFlight.current = false;
      setIsNostrSubmitting(false);
    }
  };

  return (
    <div className="flex flex-col items-center gap-6 flex-1">
      <h1 className="text-[36px] font-bold text-white">
        Login
      </h1>

      <Button
        variant={ButtonVariant.alwaysWhite}
        icon={IconName.EMAIL}
        onClick={() => navigate('/login/email', {
          state: {
            addAccount: location.state?.addAccount
          }
        })}
      >
        Login with Email
      </Button>

      {hasNostrExtension && (
        <Button
          variant={ButtonVariant.purple}
          icon={IconName.NOSTR_LOGO}
          iconSize={28}
          iconColor="#FFFFFF"
          onClick={() => handleNip07Auth(false)}
          loading={isNostrSubmitting}
          className='h-[49px] w-full px-3'
          size={null}
        >
          Login with Nostr Extension (NIP-07)
        </Button>
      )}

      <Button
        variant={ButtonVariant.purple}
        icon={IconName.NOSTR_LOGO}
        iconSize={28}
        iconColor="#FFFFFF"
        onClick={() => navigate('/login/nostr', {
          state: {
            addAccount: location.state?.addAccount
          }
        })}
      >
        Login with Nostr
      </Button>

      <LinkSignUp />
    </div>
  );
};

export default ChooseAuthType;
